import React, { useEffect, useState } from 'react';
import { MealType, UserType } from '../../common/commonTypes';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import LoadingButton from '@mui/lab/LoadingButton';
import { createFavoriteMeal, saveMeal, updateFavoriteMeal, editMeal } from '../../api';
import { makeMealsForSaving } from '../HomeTab/helpers';
import { FormControlLabel, IconButton, InputAdornment, TextField } from '@mui/material';
import Switch from '@mui/material/Switch';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';

type ModalSaveMealType = {
    user: UserType;
    meals: MealType[];
    actionType: 'CREATE_FAV' | 'EDIT_FAV' | 'LOG_MEALS' | 'EDIT_MEAL' | 'SAVE_SOME_FAV';
    onCancel: () => void;
    onSuccess: (mealName?: string) => void;
};

const imageUrls = {
    LOG_MEALS: '/calPal_home_transp_s_cut.png',
    EDIT_MEAL: '/calPal_home_transp_s_cut.png',

    CREATE_FAV: '/calPal_fav_transp_2_s_cut.png',
    EDIT_FAV: '/calPal_fav_transp_2_s_cut.png',
    SAVE_SOME_FAV: '/calPal_fav_transp_2_s_cut.png',
};

const ModalSaveMeal = (props: ModalSaveMealType) => {
    const { user, meals, actionType, onCancel, onSuccess } = props;

    const isEditableMode = actionType !== 'CREATE_FAV';

    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [mealsToSave, setMealsToSave] = useState<MealType[]>(meals);
    const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(true);
    const [isEditAllDisabled, setIsEditAllDisabled] = useState<boolean>(isEditableMode);

    const { t, i18n } = useTranslation();

    useEffect(() => {
        const isDisabled = mealsToSave.some(
            (meal) =>
                !meal.name ||
                !meal.weight ||
                !meal.calories ||
                meal.weight.toString() === '0' ||
                meal.calories.toString() === '0'
        );
        setIsSaveDisabled(isDisabled);
    }, [mealsToSave]);

    const confirmSave = async () => {
        setLoading(true);
        setErrorMessage('');

        try {
            if (!actionType) {
                setErrorMessage('No actionType');
            }

            if (actionType === 'LOG_MEALS' || actionType === 'SAVE_SOME_FAV') {
                const confirmedMealsToSave = makeMealsForSaving(mealsToSave, user.id);
                await saveMeal(confirmedMealsToSave);

                if (actionType === 'SAVE_SOME_FAV') {
                    onSuccess(confirmedMealsToSave[0].name);
                } else {
                    onSuccess();
                }
            }

            if (actionType === 'CREATE_FAV') {
                const confirmedMealsToFav = makeMealsForSaving(mealsToSave, user.id);
                await createFavoriteMeal(confirmedMealsToFav[0]);
                onSuccess();
            }

            if (actionType === 'EDIT_FAV') {
                const mealId = mealsToSave[0]._id;
                const confirmedFavsToEdit = makeMealsForSaving(mealsToSave, user.id);
                await updateFavoriteMeal(mealId as string, confirmedFavsToEdit[0]);
                onSuccess(confirmedFavsToEdit[0].name);
            }

            if (actionType === 'EDIT_MEAL') {
                const mealId = mealsToSave[0]._id;
                const confirmedMealsToEdit = makeMealsForSaving(mealsToSave, user.id);
                await editMeal(mealId as string, confirmedMealsToEdit[0]);
                onSuccess(confirmedMealsToEdit[0].name);
            }

            setLoading(false);

            window.gtag?.('event', `modalAction - ${actionType}`, {});
        } catch (error: any) {
            const message =
                error.response.data.message || error.response.data.error || 'Error on saving';
            console.log(
                '%c Error saving meal: >>> ',
                'background: #900; color: #ffffff',
                error.response.data
            );
            setErrorMessage(message);
            setLoading(false);
        }
    };

    const onFieldChange = (e: any, idx: number, fieldName: string) => {
        const { value } = e.target;
        const newMeals = [...mealsToSave];

        if (isEditAllDisabled && fieldName === 'weight' && value !== '') {
            const newWeight = Math.round(parseFloat(value));
            const factor = newWeight / (meals[idx].weight || 1);

            newMeals[idx] = {
                ...newMeals[idx],
                weight: newWeight,
                calories: Math.round(meals[idx].calories * factor),
                protein: Math.round(meals[idx].protein * factor),
                fats: Math.round(meals[idx].fats * factor),
                carbsNoSugars: Math.round(meals[idx].carbsNoSugars * factor),
                sugars: Math.round(meals[idx].sugars * factor),
            };
        } else {
            newMeals[idx] = { ...newMeals[idx], [fieldName]: value };
        }

        setMealsToSave(newMeals);
    };

    const onDelete = (idx: number) => {
        const newMeals = [...mealsToSave];
        newMeals.splice(idx, 1);
        setMealsToSave(newMeals);
    };

    const TEXTS: any = {
        CREATE_FAV: {
            title: t('CREATE_FAV_title'),
        },
        EDIT_FAV: {
            title: t('EDIT_FAV_title'),
        },
        EDIT_MEAL: {
            title: t('EDIT_MEAL_title'),
        },
        LOG_MEALS: {
            title: t('LOG_MEALS_title'),
        },
        SAVE_SOME_FAV: {
            title: t('SAVE_SOME_FAV_title'),
        },
    };

    return (
        <Dialog
            open
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    onCancel();
                }
            }}
        >
            <img src={imageUrls[actionType]} className="absolute bottom-0 left-0" />

            <DialogTitle>{TEXTS[actionType].title}</DialogTitle>

            <DialogContent>
                {Boolean(errorMessage) && <Alert severity="error">{errorMessage}</Alert>}

                {isEditableMode && (
                    <div className="mb-4">
                        <FormControlLabel
                            label={t('unlock_toggle')}
                            control={
                                <Switch
                                    checked={!isEditAllDisabled}
                                    onChange={() => setIsEditAllDisabled(!isEditAllDisabled)}
                                />
                            }
                        />
                    </div>
                )}

                <ul>
                    {mealsToSave.map((meal, idx) => (
                        <li
                            key={idx}
                            className={`flex my-2 ${mealsToSave.length > 1 && idx !== mealsToSave.length - 1 && 'border-b-2 border-gray-700 pb-4 mb-6'}`}
                        >
                            <div>
                                {mealsToSave.length > 1 && !isEditAllDisabled && (
                                    <IconButton
                                        style={{ margin: '10px 0 0 -24px' }}
                                        aria-label="delete"
                                        color="default"
                                        size="small"
                                        onClick={(e) => {
                                            onDelete(idx);
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                )}
                            </div>

                            <div>
                                <div className="mb-4">
                                    <TextField
                                        label={t('name')}
                                        fullWidth
                                        variant="outlined"
                                        required
                                        value={meal.name}
                                        onChange={(e) => onFieldChange(e, idx, 'name')}
                                    />
                                </div>

                                <div className="mb-4 flex justify-between">
                                    <TextField
                                        style={{ width: '48%' }}
                                        label={t('weight')}
                                        variant="standard"
                                        size="small"
                                        type="number"
                                        required
                                        value={meal.weight}
                                        onChange={(e: any) => onFieldChange(e, idx, 'weight')}
                                        slotProps={{
                                            input: {
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        {t('gr')}
                                                    </InputAdornment>
                                                ),
                                            },
                                            htmlInput: { min: 0 },
                                        }}
                                    />

                                    <TextField
                                        style={{ width: '48%' }}
                                        label={t('calories')}
                                        variant="standard"
                                        type="number"
                                        size="small"
                                        required
                                        disabled={isEditAllDisabled}
                                        value={meal.calories}
                                        onChange={(e) => onFieldChange(e, idx, 'calories')}
                                        slotProps={{
                                            input: {
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        {t('cal')}
                                                    </InputAdornment>
                                                ),
                                            },
                                            htmlInput: { min: 0 },
                                        }}
                                    />
                                </div>

                                <div className="mb-4 flex justify-between">
                                    <TextField
                                        style={{ width: '48%' }}
                                        label={t('protein')}
                                        variant="standard"
                                        size="small"
                                        type="number"
                                        disabled={isEditAllDisabled}
                                        value={meal.protein}
                                        onChange={(e) => onFieldChange(e, idx, 'protein')}
                                        slotProps={{
                                            input: {
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        {t('gr')}
                                                    </InputAdornment>
                                                ),
                                            },
                                            htmlInput: { min: 0 },
                                        }}
                                    />

                                    <TextField
                                        style={{ width: '48%' }}
                                        label={t('fat')}
                                        variant="standard"
                                        size="small"
                                        value={meal.fats}
                                        type="number"
                                        disabled={isEditAllDisabled}
                                        onChange={(e) => onFieldChange(e, idx, 'fats')}
                                        slotProps={{
                                            input: {
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        {t('gr')}
                                                    </InputAdornment>
                                                ),
                                            },
                                            htmlInput: { min: 0 },
                                        }}
                                    />
                                </div>

                                <div className="mb-4 flex justify-between">
                                    <TextField
                                        label={t('carbs_excl_sugar')}
                                        style={{ width: '48%' }}
                                        variant="standard"
                                        size="small"
                                        type="number"
                                        disabled={isEditAllDisabled}
                                        value={meal.carbsNoSugars}
                                        onChange={(e) => onFieldChange(e, idx, 'carbsNoSugars')}
                                        slotProps={{
                                            input: {
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        {t('gr')}
                                                    </InputAdornment>
                                                ),
                                            },
                                            htmlInput: { min: 0 },
                                        }}
                                    />

                                    <TextField
                                        style={{ width: '48%' }}
                                        label={t('sugar')}
                                        variant="standard"
                                        size="small"
                                        type="number"
                                        disabled={isEditAllDisabled}
                                        value={meal.sugars}
                                        onChange={(e) => onFieldChange(e, idx, 'sugars')}
                                        slotProps={{
                                            input: {
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        {t('gr')}
                                                    </InputAdornment>
                                                ),
                                            },
                                            htmlInput: { min: 0 },
                                        }}
                                    />
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            </DialogContent>

            <DialogActions style={{ position: 'relative' }}>
                <LoadingButton onClick={onCancel} disabled={loading}>
                    {t('MODAL_cancel')}
                </LoadingButton>
                <LoadingButton
                    onClick={confirmSave}
                    loading={loading}
                    disabled={isSaveDisabled}
                    variant="contained"
                    color="primary"
                >
                    {actionType === 'SAVE_SOME_FAV' ? t('MODAL_log') : t('MODAL_save')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default ModalSaveMeal;
